import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { EuiButton, EuiFieldText } from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';
import './YouTubePlaylistImport.css';

const errorMessage = error => error.response?.data?.message || '讀取失敗，請稍後再試。';

export default function YouTubePlaylistImport({ playlistId, existingSongs, onImported }) {
  const tr = text => TranslateLanguage(localStorage.userLanguage, text);
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const existingIds = useMemo(() => new Set((existingSongs || []).map(song => song.resourceId)), [existingSongs]);
  const selectedCount = videos.filter(video => selectedIds.has(video.videoId) && !existingIds.has(video.videoId)).length;
  const availableIds = videos.filter(video => !existingIds.has(video.videoId)).map(video => video.videoId);

  const loadPlaylist = async () => {
    if (!url.trim()) { setError(tr('請先貼上 YouTube 播放列表連結。')); return; }
    setLoading(true); setError(''); setPreview(null); setVideos([]); setSelectedIds(new Set());
    try {
      const { data } = await axios.get('/api/playlists/youtube-preview', { params: { url: url.trim() } });
      setPreview(data);
      setVideos(data.videos || []);
      setSelectedIds(new Set((data.videos || []).filter(video => !existingIds.has(video.videoId)).map(video => video.videoId)));
    } catch (error) { setError(tr(errorMessage(error))); }
    finally { setLoading(false); }
  };

  const importSelected = async () => {
    const selected = videos.filter(video => selectedIds.has(video.videoId) && !existingIds.has(video.videoId))
      .map(({ videoId, title }) => ({ videoId, title: title.trim() }));
    if (!selected.length || selected.some(video => !video.title)) {
      setError(tr('請選擇至少一首有歌名的詩歌。'));
      return;
    }
    setImporting(true); setError('');
    try {
      const { data } = await axios.post(`/api/playlists/${playlistId}/import-youtube`, { videos: selected });
      onImported(data);
    } catch (error) { setError(tr(errorMessage(error))); }
    finally { setImporting(false); }
  };

  return <div className="youtube-playlist-import">
    <p>{tr('貼上公開 YouTube 播放列表連結，檢查歌名後選擇要加入的詩歌。')}</p>
    <div className="youtube-playlist-import-url">
      <EuiFieldText fullWidth aria-label={tr('YouTube 播放列表連結')} placeholder="https://www.youtube.com/playlist?list=..." value={url}
        onChange={event => { setUrl(event.target.value); setPreview(null); setVideos([]); setSelectedIds(new Set()); setError(''); }} />
      <EuiButton onClick={loadPlaylist} isLoading={loading} disabled={importing}>{tr('讀取歌單')}</EuiButton>
    </div>
    {error && <p className="youtube-playlist-import-error" role="alert">{error}</p>}
    {preview && <>
      <div className="youtube-playlist-import-heading">
        <div><strong>{preview.title}</strong><small>{tr(`找到 ${videos.length} 首影片；${videos.length - availableIds.length} 首已在歌單中`)}</small></div>
        <div className="youtube-playlist-import-select-actions">
          <button type="button" onClick={() => setSelectedIds(new Set(availableIds))}>{tr('全選')}</button>
          <button type="button" onClick={() => setSelectedIds(new Set())}>{tr('全不選')}</button>
        </div>
      </div>
      {preview.truncated && <p className="youtube-playlist-import-warning">{tr('播放列表較長，目前只讀取前 500 首可用影片。')}</p>}
      {videos.length === 0 ? <p>{tr('沒有可加入的公開影片。')}</p> : <div className="youtube-playlist-import-list">
        {videos.map((video, index) => {
          const duplicate = existingIds.has(video.videoId);
          return <div className={`youtube-playlist-import-row${duplicate ? ' is-duplicate' : ''}`} key={video.videoId}>
            <input type="checkbox" aria-label={tr(`選取第 ${index + 1} 首：${video.title}`)} checked={!duplicate && selectedIds.has(video.videoId)} disabled={duplicate || importing}
              onChange={event => setSelectedIds(current => {
                const next = new Set(current);
                if (event.target.checked) next.add(video.videoId); else next.delete(video.videoId);
                return next;
              })} />
            <span className="youtube-playlist-import-position">{index + 1}</span>
            <input className="youtube-playlist-import-title" aria-label={tr(`第 ${index + 1} 首歌名`)} value={video.title} maxLength={300}
              disabled={duplicate || importing} onChange={event => setVideos(current => current.map(item =>
                item.videoId === video.videoId ? { ...item, title: event.target.value } : item))} />
            {duplicate && <small>{tr('已加入')}</small>}
          </div>;
        })}
      </div>}
      <div className="youtube-playlist-import-footer">
        <span>{tr(`已選 ${selectedCount} 首`)}</span>
        <EuiButton fill onClick={importSelected} isLoading={importing} disabled={!selectedCount || loading}>{tr('加入所選詩歌')}</EuiButton>
      </div>
    </>}
  </div>;
}
